export const ACTIVATION_SECRET_FRAGMENT_KEY = "forgeRemoteActivation";

export interface BootstrapSecretLease {
  readonly available: boolean;
  use<T>(visitor: (secret: string) => T): T;
  clear(): void;
}

export class InMemoryBootstrapSecret implements BootstrapSecretLease {
  #secret: string | null = null;

  get available() {
    return this.#secret !== null;
  }

  capture(secret: string) {
    if (secret.length === 0) return false;
    this.#secret = secret;
    return true;
  }

  use<T>(visitor: (secret: string) => T): T {
    if (this.#secret === null) throw new Error("This activation link is missing or has already been redeemed.");
    return visitor(this.#secret);
  }

  clear() {
    this.#secret = null;
  }
}

interface NavigationLocation {
  href: string;
}

interface NavigationHistory {
  state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export function captureBootstrapSecretFromNavigation(
  lease: InMemoryBootstrapSecret,
  location: NavigationLocation,
  history: NavigationHistory,
) {
  const url = new URL(location.href);
  const fragmentParameters = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
  const secrets = fragmentParameters.getAll(ACTIVATION_SECRET_FRAGMENT_KEY);
  const containedSecret = secrets.length > 0;
  if (containedSecret) lease.clear();
  if (secrets.length === 1 && secrets[0].length > 0) lease.capture(secrets[0]);

  if (containedSecret) {
    url.hash = "";
    history.replaceState(history.state, "", `${url.pathname}${url.search}`);
  }

  return containedSecret;
}

export const bootstrapSecretLease = new InMemoryBootstrapSecret();

export function captureBootstrapSecretFromCurrentUrl() {
  if (typeof window === "undefined") return false;
  return captureBootstrapSecretFromNavigation(bootstrapSecretLease, window.location, window.history);
}
